import { keyframes } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import type { SidekickIconState } from './SidekickDotsIcon.types';

/**
 * Resting dot radius in SVG user units (viewBox 24x24). Derived from
 * the designer WebP reference: ~30px dot in a 600px canvas, scaled to
 * a 24-unit grid (30 / 600 * 24 = 1.2); rounded up a touch for
 * perceived weight on a white surface.
 */
const BASE_R = 1.25;
/**
 * Dot radius at pulse peak. Derived from the designer WebP: ~72px in a
 * 600px canvas (72 / 600 * 24 = 2.88); rounded to a clean 3 to match
 * the static Figma frames on the Sidekick icon spec.
 */
const PEAK_R = 3;

/** Shared easing for the rise/fall of every pulse envelope. */
const PULSE_EASE = 'cubic-bezier(0.45, 0, 0.55, 1)';

/** Full loop durations, copied frame-for-frame from the WebP specs. */
const STANDBY_LOOP_MS = 8050;
const LISTENING_LOOP_MS = 4000;
const THINKING_PERIOD_MS = 1800;

/**
 * Builds the four `standby` keyframe tracks. The WebP reference plays
 * two full waves per 8050ms loop (peaks at ~1000ms and ~5000ms); each
 * wave staggers BL first, then TL+BR together, then TR. Both `r` and
 * `fill` animate together on the same envelope so the dot appears to
 * "fill in" as it grows.
 */
const buildStandby = (rest: string, peak: string) => {
  // Percentages below are time-in-loop / 8050ms for each wave milestone.
  // Wave 1: start 380ms, peak 1000ms, end 2400ms.
  // Wave 2: start 4400ms, peak 5000ms, end 6400ms.
  //
  // Per-corner offsets vs wave start (BL leads the wave):
  //   BL  = +0ms       → peak in-wave at 1000ms and 5000ms
  //   TL  = +400ms     → peaks at 1400ms and 5400ms
  //   BR  = +400ms     → peaks at 1400ms and 5400ms (paired with TL)
  //   TR  = +600ms     → peaks at 1600ms and 5600ms
  //
  // Silent rest: 2400→4400 (~2000ms) and 6400→8050 (~1650ms).

  return {
    bl: keyframes`
      0%    { r: ${BASE_R}px; fill: ${rest}; }
      4.7%  { r: ${BASE_R}px; fill: ${rest}; }
      12.4% { r: ${PEAK_R}px; fill: ${peak}; }
      29.8% { r: ${BASE_R}px; fill: ${rest}; }
      54.7% { r: ${BASE_R}px; fill: ${rest}; }
      62.1% { r: ${PEAK_R}px; fill: ${peak}; }
      79.5% { r: ${BASE_R}px; fill: ${rest}; }
      100%  { r: ${BASE_R}px; fill: ${rest}; }
    `,
    tlbr: keyframes`
      0%    { r: ${BASE_R}px; fill: ${rest}; }
      9.7%  { r: ${BASE_R}px; fill: ${rest}; }
      17.4% { r: ${PEAK_R}px; fill: ${peak}; }
      34.8% { r: ${BASE_R}px; fill: ${rest}; }
      59.6% { r: ${BASE_R}px; fill: ${rest}; }
      67.1% { r: ${PEAK_R}px; fill: ${peak}; }
      84.4% { r: ${BASE_R}px; fill: ${rest}; }
      100%  { r: ${BASE_R}px; fill: ${rest}; }
    `,
    tr: keyframes`
      0%    { r: ${BASE_R}px; fill: ${rest}; }
      12.1% { r: ${BASE_R}px; fill: ${rest}; }
      19.9% { r: ${PEAK_R}px; fill: ${peak}; }
      37.3% { r: ${BASE_R}px; fill: ${rest}; }
      62.1% { r: ${BASE_R}px; fill: ${rest}; }
      69.6% { r: ${PEAK_R}px; fill: ${peak}; }
      86.9% { r: ${BASE_R}px; fill: ${rest}; }
      100%  { r: ${BASE_R}px; fill: ${rest}; }
    `,
  };
};

/**
 * `listening` envelope — all four dots synchronous, two pulses per
 * 4000ms loop (peaks at 600ms and 2600ms).
 */
const buildListening = (rest: string, peak: string) => keyframes`
  0%    { r: ${BASE_R}px; fill: ${rest}; }
  15%   { r: ${PEAK_R}px; fill: ${peak}; }
  45%   { r: ${BASE_R}px; fill: ${rest}; }
  50%   { r: ${BASE_R}px; fill: ${rest}; }
  65%   { r: ${PEAK_R}px; fill: ${peak}; }
  95%   { r: ${BASE_R}px; fill: ${rest}; }
  100%  { r: ${BASE_R}px; fill: ${rest}; }
`;

/**
 * `thinking` envelope — one peak per 1800ms period per dot. Each dot
 * uses `animation-delay` to stagger the cascade TL → TR → BR → BL.
 */
const buildThinking = (rest: string, peak: string) => keyframes`
  0%    { r: ${BASE_R}px; fill: ${rest}; }
  33%   { r: ${PEAK_R}px; fill: ${peak}; }
  66%   { r: ${BASE_R}px; fill: ${rest}; }
  100%  { r: ${BASE_R}px; fill: ${rest}; }
`;

/**
 * Phase offsets (ms) for the clockwise cascade in `thinking`. TL leads,
 * TR follows 400ms later, BR at 800ms, BL at 1000ms. Matches the WebP
 * reference where each dot peaks exactly 400ms after its predecessor
 * (TL→TR→BR = +400ms each) with BL catching up +200ms after BR.
 */
const THINKING_DELAY_BY_CORNER = {
  TL: 0,
  TR: 400,
  BR: 800,
  BL: 1000,
} as const;

type Corner = keyof typeof THINKING_DELAY_BY_CORNER;

type StyleParams = { state: SidekickIconState };

const useStyles = makeStyles<StyleParams>({
  name: 'SidekickDotsIcon',
})((theme, { state }) => {
  // Rest color: Figma `color/brand/300` (#a9d6ff) — pale-blue idle tint.
  // Peak color: Figma `color/brand/600` (#0072ce) — primary brand blue.
  const REST = theme.new.color.brand[300] as string;
  const PEAK = theme.new.color.brand[600] as string;

  const standby = buildStandby(REST, PEAK);
  const listening = buildListening(REST, PEAK);
  const thinking = buildThinking(REST, PEAK);

  const animationFor = (corner: Corner): string => {
    switch (state) {
      case 'listening':
        return `${listening} ${LISTENING_LOOP_MS}ms ${PULSE_EASE} infinite`;
      case 'thinking':
        return `${thinking} ${THINKING_PERIOD_MS}ms ${PULSE_EASE} ${THINKING_DELAY_BY_CORNER[corner]}ms infinite`;
      default: {
        const track =
          corner === 'TR'
            ? standby.tr
            : corner === 'BL'
              ? standby.bl
              : standby.tlbr;
        return `${track} ${STANDBY_LOOP_MS}ms ${PULSE_EASE} infinite`;
      }
    }
  };

  return {
    root: {
      display: 'block',
      color: PEAK,
      '@media (prefers-reduced-motion: reduce)': {
        '& circle': {
          animation: 'none !important',
          fill: PEAK,
        },
      },
    },
    dotTL: { animation: animationFor('TL') },
    dotTR: { animation: animationFor('TR') },
    dotBR: { animation: animationFor('BR') },
    dotBL: { animation: animationFor('BL') },
  };
});

export default useStyles;
